// ABOUTME: Reusable button component to redirect users from old gnowbe-web to new gnowbe-frontend
// ABOUTME: Handles URL mapping to preserve user context when switching apps

import React from 'react'
import { Button } from 'components/utils/Button'
import { trackButtonEnhanced } from 'utils/track'
import { switchToNewApp } from 'utils/appVersion'

interface ExploreNewAppButtonProps {
  /** The screen/page name where the button is displayed (for analytics) */
  onScreen?: string
  /** Button type (default: 'gnowbe') */
  type?: 'gnowbe' | 'sub' | 'primary' | 'text' | 'guide' | 'danger' | 'team' | 'quote' | 'edit' | 'chat' | 'ad' | 'magic'
  /** Custom button text (default: 'Explore the New Web App') */
  text?: string
  /** Additional CSS classes */
  className?: string
  /**
   * The group (companyId) the learner is currently studying under. Handed to the
   * new app as `?companyId=…` so the switch keeps the learner's scope — see the
   * note on handleClick for why this matters.
   */
  companyId?: string
}

// Company ids are 17-char alphanumerics. Guard the value we emit: a component that
// renders before its data resolves can stringify `undefined`, and `?companyId=undefined`
// on the receiving side would be persisted as a real group and then sent on every
// study/progress write. The new app rejects malformed values too, but emitting a
// clean URL keeps the failure from ever leaving this app.
const COMPANY_ID_SHAPE = /^[a-z0-9]{10,30}$/i

/**
 * Path prefixes worth carrying the group on — the ones the new app rewrites to a
 * program route (`/programs/{courseId}/…`) and can therefore key the group to:
 *
 *   /learn/*    → /programs/{courseId}/sessions/…   (course from the user profile)
 *   /details/*  → /programs/{courseId}
 *
 * Everywhere else the param is not just useless, it is harmful. The new app applies
 * the group only when the rewritten path contains a courseId, and it cleans the param
 * out of the address bar *as part of applying it*. So on a non-program page (Home is
 * the common one — `/home` rewrites to `/`) the param is never consumed and therefore
 * never cleaned: a group id would simply sit in the URL, ready to be copied into a
 * shared link.
 *
 * `/edit/{companyId}-{courseId}` and `/activity/{companyId}/{courseId}` already carry
 * the company in the path, so they need nothing here.
 *
 * On Home the value would be meaningless anyway: it comes from
 * `currentCompanyIdCourseId`, i.e. the group of the last course opened, not anything
 * about the page being viewed.
 */
const PROGRAM_SCOPED_PATH_PREFIXES = ['/learn', '/details']

const isProgramScopedPath = (pathname: string): boolean =>
  PROGRAM_SCOPED_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  )

/**
 * Button that switches a user from the old gnowbe-web app to the new gnowbe-frontend app
 * while preserving their current context/view.
 *
 * When clicked:
 * 1. Tracks an analytics event
 * 2. Appends `?companyId=…` when we know the learner's current group
 * 3. switchToNewApp: sets app_version=new (2 years) — CloudFront reads this cookie to
 *    decide which bundle to serve, so the switch is a cookie write plus a reload of the
 *    SAME url — then evicts this app's service worker + caches and reloads, so the edge
 *    serves the new app for the current path
 *
 * Why step 2 exists: the new app's routes are program-scoped only
 * (/programs/:programId/sessions/:sid/actions/:aid) and carry no group. Because this
 * button reloads the current url in place, a learner enrolled in the same course
 * through SEVERAL groups arrives with no way for the new app to know which one they
 * were in. The new app then falls back to the eventually-consistent
 * profile.currentCompanyIdCourseId — which can be stale, or point at a different
 * course entirely — and study/progress writes go out unscoped or under the wrong
 * group. Carrying the group explicitly removes the guess.
 *
 * The new app ignores the param when it is absent or malformed, so this is safe to
 * ship independently and old links keep working.
 */
export const ExploreNewAppButton: React.FC<ExploreNewAppButtonProps> = ({
  onScreen = 'Unknown',
  type = 'gnowbe',
  text = 'Switch back to our New App',
  className,
  companyId,
}) => {
  const handleClick = async () => {
    // Track button click for analytics
    trackButtonEnhanced({
      button: text,
      onScreen,
    })

    // Carry the current group across the handoff, but ONLY from a page the new app
    // can key it to (see isProgramScopedPath). Rewrite the address bar first so the
    // reload re-requests the url WITH the param (mirrors how the new app builds the
    // legacy url on the way back). Synchronous, so it lands before the awaited
    // eviction below can be interrupted.
    if (companyId && COMPANY_ID_SHAPE.test(companyId)) {
      try {
        const url = new URL(window.location.href)
        if (isProgramScopedPath(url.pathname)) {
          url.searchParams.set('companyId', companyId)
          window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
        }
      } catch {
        // Never block the switch on url handling — fall through to a plain reload.
      }
    }

    // Cookie flip, service-worker eviction and reload (see switchToNewApp).
    await switchToNewApp()
  }

  return (
    <Button
      type={type}
      text={text}
      onClick={handleClick}
      className={className}
    />
  )
}
